/* eslint-disable react/no-array-index-key */
import { Flipped, Flipper } from "react-flip-toolkit";
import { makeStyles } from "tss-react/mui";
import { Box, Divider, Typography } from "@mui/material";
import { MiCardList } from "../MiCardList/MiCardList";

/**
 * @component
 * @param {Object} props - The component props
 * @param {Array<Object>} [props.data=[]] - Array of data items to render as cards
 * @param {Array<Object>} [props.recentData=[]] - Array of recent data items to display in a separate "Recently Created/Modified" section
 * @param {React.ComponentType} [props.CardComponent] - Custom card component to use instead of the default MiCardList
 * @param {string} [props.testID=""] - Test ID prefix for the component and its elements
 * @param {Object} [props.cardProps={}] - Props to pass through to each card component
 * @param {Object} [props.cardProps.containerStyles] - Custom styles for card containers
 * @param {Function} [props.cardProps.onClick] - Click handler for cards
 * @param {Array<Object>} [props.cardProps.sections] - Section configuration for cards
 * @param {Object} [props.cardProps.leftCardProps] - Left card configuration
 * @param {Function} [props.cardProps.renderItemActions] - Function to render card actions
 * @param {boolean} [props.cardProps.showNulls] - Whether to show null values in cards
 * @param {string} [props.cardProps.titleField] - Field to use as card title
 * @param {string} [props.entityName] - Name of the entity being displayed (ie "Users")
 * @returns {JSX.Element|null} The rendered list of cards, or null if no data
 * @param {...any} [props.cardProps.*] - Any other props to pass to card components
 *
 */
export function MiListCards({
  data = [],
  recentData = [],
  CardComponent,
  testID = "",
  cardProps = {},
  entityName,
}) {
  const { classes } = useStyles();

  if (!data?.length) {
    return null;
  }

  return (
    <Box
      className={classes.cardListContainer}
      data-testid={`${testID}-MiListCards`}
    >
      {recentData?.length > 0 ? (
        <Box paddingBottom={2}>
          <Typography variant="HD3" color="primary">
            Recently Modified
          </Typography>
          <Divider />
          {recentData?.map((item, i) =>
            CardComponent ? (
              <CardComponent
                key={`${item.id}-${i}-recent`}
                item={item}
                index={i}
                {...cardProps}
              />
            ) : (
              <MiCardList
                key={`${item.id}-${i}-recent`}
                item={item}
                index={i}
                {...cardProps}
                containerStyles={{
                  ...(cardProps?.containerStyles ?? {}),
                }}
                rightAction={null}
              />
            )
          )}
        </Box>
      ) : null}
      <>
        {recentData?.length > 0 && data?.length > 0 ? (
          <>
            <Typography variant="HD3" color="primary">
              All {entityName ?? ""}
            </Typography>
            <Divider />
          </>
        ) : null}
        <Flipper
          flipKey={data
            .map((item) => item.id ?? JSON.stringify(item))
            .join("-")}
        >
          {data?.length > 0
            ? data?.map((item, i) =>
                CardComponent ? (
                  <Flipped key={`${item.id}-${i}`} flipId={`${item.id}`}>
                    {(flippedProps) => (
                      <CardComponent
                        item={item}
                        index={i}
                        flippedProps={flippedProps}
                        {...cardProps}
                      />
                    )}
                  </Flipped>
                ) : (
                  <Flipped key={`${item.id}-${i}`} flipId={`${item.id}`}>
                    {(flippedProps) => (
                      <MiCardList
                        item={item}
                        index={i}
                        flippedProps={flippedProps}
                        testID={`MiListCard-${i}`}
                        {...cardProps}
                      />
                    )}
                  </Flipped>
                )
              )
            : null}
        </Flipper>
      </>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  cardListContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    "& > *": {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
  },
}));
